import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { fetchSeries, fetchSeriesOdds } from '@/lib/supabase-queries';
import {
  fetchBracketEntry,
  saveBracketEntry,
  isBracketLocked,
  resolveDisplayName,
} from '@/lib/bracket-queries';
import {
  BracketPicks,
  BracketPick,
  ROUNDS_WITH_SCORE,
} from '@/types/bracket';

// ── Types ────────────────────────────────────────────────────────────────────

export type BracketConference = 'west' | 'east' | 'finals';

export interface BracketSlot {
  slotId: string;              // series DB id for R1; virtual id for R2+
  round: string;               // 'round1' | 'west_semis' | 'east_semis' | 'west_finals' | 'east_finals' | 'finals'
  conference: BracketConference;
  position: number;            // position within round (0-indexed)
  teamHome: string | null;     // null = waiting for upstream pick
  teamAway: string | null;
  teamHomeLogo: string | null;
  teamAwayLogo: string | null;
  feedsInto: string | null;    // slotId of the next round slot this feeds into
  fedBy: [string, string] | null; // [slotId1, slotId2] of R1 slots that feed into this
}

// ── NBA Playoff Bracket Structure ─────────────────────────────────────────────
//
// West: 4 R1 series → 2 R2 series → 1 West Finals → Finals
// East: 4 R1 series → 2 R2 series → 1 East Finals → Finals
//
// Virtual slot IDs for R2+ (since DB rows may not exist yet):
//   r2_west_0, r2_west_1  (West R2)
//   r2_east_0, r2_east_1  (East R2)
//   cf_west               (West Finals / Conference Finals)
//   cf_east               (East Finals / Conference Finals)
//   finals                (NBA Finals)

const WEST_R1_SLOTS = ['w_r1_0', 'w_r1_1', 'w_r1_2', 'w_r1_3'];
const EAST_R1_SLOTS = ['e_r1_0', 'e_r1_1', 'e_r1_2', 'e_r1_3'];

// Feed map: which R2 slot does each R1 pair feed?
//   w_r1_0 + w_r1_1 → r2_west_0
//   w_r1_2 + w_r1_3 → r2_west_1
//   e_r1_0 + e_r1_1 → r2_east_0
//   e_r1_2 + e_r1_3 → r2_east_1
const R1_TO_R2: Record<string, string> = {
  w_r1_0: 'r2_west_0',
  w_r1_1: 'r2_west_0',
  w_r1_2: 'r2_west_1',
  w_r1_3: 'r2_west_1',
  e_r1_0: 'r2_east_0',
  e_r1_1: 'r2_east_0',
  e_r1_2: 'r2_east_1',
  e_r1_3: 'r2_east_1',
};

// Feed map: R2 → Conference Finals
const R2_TO_CF: Record<string, string> = {
  r2_west_0: 'cf_west',
  r2_west_1: 'cf_west',
  r2_east_0: 'cf_east',
  r2_east_1: 'cf_east',
};

// Feed map: CF → Finals
const CF_TO_FINALS: Record<string, string> = {
  cf_west: 'finals',
  cf_east: 'finals',
};

const ALL_FEEDS: Record<string, string> = {
  ...R1_TO_R2,
  ...R2_TO_CF,
  ...CF_TO_FINALS,
};

// ── Build static bracket skeleton ────────────────────────────────────────────

function buildBracketSkeleton(): BracketSlot[] {
  const slots: BracketSlot[] = [];

  // West R1
  WEST_R1_SLOTS.forEach((id, i) => {
    slots.push({
      slotId: id,
      round: 'round1',
      conference: 'west',
      position: i,
      teamHome: null,
      teamAway: null,
      teamHomeLogo: null,
      teamAwayLogo: null,
      feedsInto: R1_TO_R2[id],
      fedBy: null,
    });
  });

  // East R1
  EAST_R1_SLOTS.forEach((id, i) => {
    slots.push({
      slotId: id,
      round: 'round1',
      conference: 'east',
      position: i,
      teamHome: null,
      teamAway: null,
      teamHomeLogo: null,
      teamAwayLogo: null,
      feedsInto: R1_TO_R2[id],
      fedBy: null,
    });
  });

  // West R2
  ['r2_west_0', 'r2_west_1'].forEach((id, i) => {
    const pair: [string, string] = i === 0
      ? ['w_r1_0', 'w_r1_1']
      : ['w_r1_2', 'w_r1_3'];
    slots.push({
      slotId: id,
      round: 'west_semis',
      conference: 'west',
      position: i,
      teamHome: null,
      teamAway: null,
      teamHomeLogo: null,
      teamAwayLogo: null,
      feedsInto: R2_TO_CF[id],
      fedBy: pair,
    });
  });

  // East R2
  ['r2_east_0', 'r2_east_1'].forEach((id, i) => {
    const pair: [string, string] = i === 0
      ? ['e_r1_0', 'e_r1_1']
      : ['e_r1_2', 'e_r1_3'];
    slots.push({
      slotId: id,
      round: 'east_semis',
      conference: 'east',
      position: i,
      teamHome: null,
      teamAway: null,
      teamHomeLogo: null,
      teamAwayLogo: null,
      feedsInto: R2_TO_CF[id],
      fedBy: pair,
    });
  });

  // West Finals
  slots.push({
    slotId: 'cf_west',
    round: 'west_finals',
    conference: 'west',
    position: 0,
    teamHome: null,
    teamAway: null,
    teamHomeLogo: null,
    teamAwayLogo: null,
    feedsInto: 'finals',
    fedBy: ['r2_west_0', 'r2_west_1'],
  });

  // East Finals
  slots.push({
    slotId: 'cf_east',
    round: 'east_finals',
    conference: 'east',
    position: 0,
    teamHome: null,
    teamAway: null,
    teamHomeLogo: null,
    teamAwayLogo: null,
    feedsInto: 'finals',
    fedBy: ['r2_east_0', 'r2_east_1'],
  });

  // Finals
  slots.push({
    slotId: 'finals',
    round: 'finals',
    conference: 'finals',
    position: 0,
    teamHome: null,
    teamAway: null,
    teamHomeLogo: null,
    teamAwayLogo: null,
    feedsInto: null,
    fedBy: ['cf_west', 'cf_east'],
  });

  return slots;
}

// ── Map DB series to R1 slots ─────────────────────────────────────────────────
// The DB contains real R1 series. We assign them positionally to our skeleton slots.
// West series (round='round1' or 'first_round') ordered by created_at → positions 0-3
// East series ordered by created_at → positions 0-3

type SeriesRow = {
  id: string;
  round: string;
  season: number;
  status: string | null;
  team_home: string;
  team_away: string;
  team_home_logo: string | null;
  team_away_logo: string | null;
  winner: string | null;
  final_score: string | null;
  conference: string | null;
  seed: number | null;
};

// NBA bracket: 1v8 + 4v5 meet in R2 top, 3v6 + 2v7 meet in R2 bottom.
// Map higher-seed (1..4) → R1 slot position so pairings match NBA standard.
const SEED_TO_POSITION: Record<number, number> = { 1: 0, 4: 1, 3: 2, 2: 3 };

function compareBySeedPosition(a: SeriesRow, b: SeriesRow): number {
  const pa = a.seed != null ? SEED_TO_POSITION[a.seed] ?? 99 : 99;
  const pb = b.seed != null ? SEED_TO_POSITION[b.seed] ?? 99 : 99;
  return pa - pb;
}

type SeriesOddsRow = {
  id: string;
  series_id: string | null;
  team: string;
  outcome: string;
  team_wins: number;
  opponent_wins: number;
  american_odds: number;
  decimal_odds: number | null;
  multiplier: number | null;
};

// Heuristic to guess conference from team names
// In practice, 2026 playoff teams are known; we use a static map or fall back to position
const WEST_TEAMS = new Set([
  'Oklahoma City Thunder', 'Houston Rockets', 'Los Angeles Lakers',
  'Golden State Warriors', 'Denver Nuggets', 'Memphis Grizzlies',
  'LA Clippers', 'Dallas Mavericks', 'Minnesota Timberwolves',
  'Sacramento Kings', 'Phoenix Suns', 'Utah Jazz',
  'Portland Trail Blazers', 'New Orleans Pelicans',
  // Abbreviated forms
  'Thunder', 'Rockets', 'Lakers', 'Warriors', 'Nuggets', 'Grizzlies',
  'Clippers', 'Mavericks', 'Timberwolves', 'Kings', 'Suns', 'Jazz',
  'Trail Blazers', 'Pelicans',
]);

function guessConference(teamHome: string, teamAway: string): BracketConference {
  const homeWest = WEST_TEAMS.has(teamHome);
  const awayWest = WEST_TEAMS.has(teamAway);
  if (homeWest || awayWest) return 'west';
  return 'east';
}

// ── Hydrate bracket slots with DB series data ─────────────────────────────────

function hydrateSlots(
  skeleton: BracketSlot[],
  dbSeries: SeriesRow[],
  picks: BracketPicks,
): BracketSlot[] {
  // Filter to R1 series only (exclude deprecated)
  const r1Series = dbSeries.filter(
    s =>
      (s.round === 'round1' || s.round === 'first_round') &&
      s.status !== 'deprecated',
  );

  // Separate by conference — use series.conference column if present, fall back to heuristic.
  // Sort by NBA-standard seed position so R2 pairings are (1v8+4v5) top, (3v6+2v7) bottom.
  const westSeries = r1Series
    .filter(s => (s.conference ?? guessConference(s.team_home, s.team_away)) === 'west')
    .sort(compareBySeedPosition);
  const eastSeries = r1Series
    .filter(s => (s.conference ?? guessConference(s.team_home, s.team_away)) === 'east')
    .sort(compareBySeedPosition);

  // Build a map of slotId → actual DB series id for R1 slots
  const slotToSeriesId: Record<string, string> = {};
  westSeries.forEach((s, i) => {
    const slotId = WEST_R1_SLOTS[i];
    if (slotId) slotToSeriesId[slotId] = s.id;
  });
  eastSeries.forEach((s, i) => {
    const slotId = EAST_R1_SLOTS[i];
    if (slotId) slotToSeriesId[slotId] = s.id;
  });

  // Also build reverse: DB series id → skeleton slotId (for propagation)
  const seriesIdToSlot: Record<string, string> = {};
  Object.entries(slotToSeriesId).forEach(([slotId, seriesId]) => {
    seriesIdToSlot[seriesId] = slotId;
  });

  return skeleton.map(slot => {
    // R1 slots: hydrate from DB
    if (slot.round === 'round1' || slot.round === 'first_round') {
      const seriesId = slotToSeriesId[slot.slotId];
      if (!seriesId) return slot;
      const series = dbSeries.find(s => s.id === seriesId);
      if (!series) return slot;
      return {
        ...slot,
        slotId: seriesId, // Use actual DB series ID as slotId for R1
        teamHome: series.team_home,
        teamAway: series.team_away,
        teamHomeLogo: series.team_home_logo,
        teamAwayLogo: series.team_away_logo,
      };
    }

    // R2+ slots: derive teams from upstream picks
    if (slot.fedBy) {
      const [upstreamA, upstreamB] = slot.fedBy;

      // Look up the actual slot IDs (R1 slots might have been replaced with DB series IDs)
      // We need to find the current resolved slotId for the upstream skeleton slots
      const resolvedA = slotToSeriesId[upstreamA] || upstreamA;
      const resolvedB = slotToSeriesId[upstreamB] || upstreamB;

      const pickA = picks[resolvedA];
      const pickB = picks[resolvedB];

      const teamA = pickA?.winner || null;
      const teamB = pickB?.winner || null;

      return {
        ...slot,
        teamHome: teamA,
        teamAway: teamB,
      };
    }

    return slot;
  });
}

// ── useBracket hook ──────────────────────────────────────────────────────────

export function useBracket(viewUserId?: string) {
  const { user } = useAuth();
  const { toast } = useToast();

  // Determine which user's bracket we're viewing/editing
  const authUserId = user?.id ?? null;
  const targetUserId = viewUserId || authUserId;
  const isViewingOther = !!(viewUserId && authUserId && viewUserId !== authUserId);

  const [picks, setPicks] = useState<BracketPicks>({});
  const [dbSeries, setDbSeries] = useState<SeriesRow[]>([]);
  const [oddsMap, setOddsMap] = useState<Record<string, SeriesOddsRow[]>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [displayName, setDisplayName] = useState('שחקן');
  const isLocked = isBracketLocked();

  // ── Load data on mount ───────────────────────────────────────────────────

  useEffect(() => {
    if (!targetUserId) return;

    const load = async () => {
      setLoading(true);
      try {
        // 1. Fetch all series
        const allSeries = (await fetchSeries()) as SeriesRow[];
        const activeSeries = allSeries.filter(s => s.status !== 'deprecated');
        setDbSeries(activeSeries);

        // 2. Fetch odds for R1 + Finals series
        const oddsSeriesIds = activeSeries
          .filter(s =>
            s.round === 'round1' ||
            s.round === 'first_round' ||
            s.round === 'finals',
          )
          .map(s => s.id);

        const oddsResults = await Promise.all(
          oddsSeriesIds.map(async id => ({
            seriesId: id,
            odds: (await fetchSeriesOdds(id)) as SeriesOddsRow[],
          })),
        );

        const newOddsMap: Record<string, SeriesOddsRow[]> = {};
        oddsResults.forEach(({ seriesId, odds }) => {
          newOddsMap[seriesId] = odds;
        });
        setOddsMap(newOddsMap);

        // 3. Load existing bracket entry
        const entry = await fetchBracketEntry(targetUserId);
        if (entry?.picks) {
          setPicks(entry.picks as BracketPicks);
        }

        // 4. Resolve display name for auth user (for saving)
        if (authUserId) {
          const name = await resolveDisplayName(authUserId);
          setDisplayName(name);
        }
      } catch (err) {
        console.error('Failed to load bracket:', err);
        toast({
          title: 'שגיאה בטעינת הברקט',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [targetUserId, authUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Build bracket slots ──────────────────────────────────────────────────

  const skeleton = buildBracketSkeleton();
  const bracketSlots = hydrateSlots(skeleton, dbSeries, picks);

  // ── Build a map from skeleton slot IDs to resolved (DB) slot IDs ─────────
  // Needed for propagation: skeleton IDs (w_r1_0) → DB series IDs

  const r1Series = dbSeries.filter(
    s =>
      (s.round === 'round1' || s.round === 'first_round') &&
      s.status !== 'deprecated',
  );
  const westSeries = r1Series
    .filter(s => (s.conference ?? guessConference(s.team_home, s.team_away)) === 'west')
    .sort(compareBySeedPosition);
  const eastSeries = r1Series
    .filter(s => (s.conference ?? guessConference(s.team_home, s.team_away)) === 'east')
    .sort(compareBySeedPosition);

  const skeletonToDbId: Record<string, string> = {};
  westSeries.forEach((s, i) => {
    const skId = WEST_R1_SLOTS[i];
    if (skId) skeletonToDbId[skId] = s.id;
  });
  eastSeries.forEach((s, i) => {
    const skId = EAST_R1_SLOTS[i];
    if (skId) skeletonToDbId[skId] = s.id;
  });

  // Reverse: DB id → skeleton id
  const dbIdToSkeleton: Record<string, string> = {};
  Object.entries(skeletonToDbId).forEach(([skId, dbId]) => {
    dbIdToSkeleton[dbId] = skId;
  });

  // ── Forward propagation ──────────────────────────────────────────────────

  const setPickWithPropagation = useCallback(
    (slotId: string, pick: BracketPick | null) => {
      if (isLocked || isViewingOther) return;

      setPicks(prev => {
        const next = { ...prev };

        // Get old winner before update
        const oldWinner = prev[slotId]?.winner;

        // Apply new pick
        if (pick === null) {
          delete next[slotId];
        } else {
          next[slotId] = pick;
        }

        const newWinner = pick?.winner;

        // If winner changed, clear downstream picks that depended on oldWinner
        if (oldWinner !== newWinner) {
          // Get skeleton ID for this slot (in case it's a DB id)
          const skeletonId = dbIdToSkeleton[slotId] || slotId;

          // Cascade clearance downstream
          const cascade = (fromSkeletonId: string, clearedTeam: string | undefined) => {
            if (!clearedTeam) return;

            const downstreamId = ALL_FEEDS[fromSkeletonId];
            if (!downstreamId) return;

            const downstreamPick = next[downstreamId];
            if (downstreamPick?.winner === clearedTeam) {
              delete next[downstreamId];
              // Continue cascading
              cascade(downstreamId, clearedTeam);
            }
          };

          cascade(skeletonId, oldWinner);
        }

        return next;
      });
    },
    [isLocked, isViewingOther, dbIdToSkeleton],
  );

  // ── Save picks ───────────────────────────────────────────────────────────

  const savePicks = useCallback(async () => {
    if (!authUserId || isLocked || isViewingOther) return;

    setSaving(true);
    try {
      // Build odds snapshot from current picks
      const oddsSnapshot: Record<string, unknown> = {};
      Object.entries(picks).forEach(([slotId, pick]) => {
        const slotOdds = oddsMap[slotId];
        if (slotOdds && pick.predicted_score) {
          const matchingOdd = slotOdds.find(
            o => o.outcome === pick.predicted_score && o.team === pick.winner,
          );
          if (matchingOdd) {
            oddsSnapshot[slotId] = {
              american_odds: matchingOdd.american_odds,
              decimal_odds: matchingOdd.decimal_odds,
              multiplier: matchingOdd.multiplier,
            };
          }
        }
      });

      await saveBracketEntry(authUserId, picks, oddsSnapshot, displayName);
      toast({ title: 'הברקט נשמר בהצלחה!' });
    } catch (err) {
      console.error('Failed to save bracket:', err);
      toast({ title: 'שגיאה בשמירת הברקט', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }, [authUserId, isLocked, isViewingOther, picks, oddsMap, displayName, toast]);

  // ── Compute progress ─────────────────────────────────────────────────────

  const totalSlots = 15;
  const filledSlots = Object.keys(picks).length;

  return {
    picks,
    setPick: setPickWithPropagation,
    savePicks,
    seriesList: dbSeries,
    oddsMap,
    loading,
    saving,
    isLocked,
    isViewingOther,
    bracketSlots,
    filledSlots,
    totalSlots,
    displayName,
  };
}

export type UseBracketReturn = ReturnType<typeof useBracket>;
